/// <amd-module name="Build/ArtifactsView/Components/ArtifactsTabView" />
import * as React from "react";

import * as Resources from "Build/ArtifactsView/Resources";
import { ObservableValue, ObservableCollection } from "VSS/Core/Observable";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IVssHistoryService } from "VSS/Platform/History";
import { RouteParameters } from "Build/Client/Linking"
import { onClickFPS } from "VSS/Platform/FPS";
import { TabBar, TabSize, IVssContributedTab, TabContent } from "VSSUI/Tabs";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Page } from "VSSUI/Page";
import { Components, VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { Header, TitleSize } from "VSSUI/Header";
import { NavConstants } from "Build/Common/Library/Constants";
import { ContainerImageDetails } from "Build/ArtifactsView/Components/ContainerImageDetails";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ArtifactsData } from "Build/Client/Contracts/DataProvider";

export interface IArtifactTabViewProps extends IVssComponentProperties {
}

export interface IArtifactsTabViewState {
    selectedTab: string;
}

export interface IRunArtifactsBackButtonProps {
    setBuildId: (buildId: number) => void;
}

export default class ArtifactsTabView extends VssComponent<IArtifactTabViewProps, IArtifactsTabViewState> {

    constructor(props: IArtifactTabViewProps, context: IVssComponentContext) {
        super(props, context);
        this.historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.tabProviders.push(getTabs(context.pageContext, "ms.vss-build-web.run-artifacts-details-view-tabs"));
        this._getArtifactsCount().then( artifactsCounts => {
            if(artifactsCounts && artifactsCounts.publishedArtifactsCount > 0) {
                this.defaultTab = NavConstants.publishedArtifacts;
            }
            else if (artifactsCounts && artifactsCounts.consumedArtifactsCount > 0) {
                this.defaultTab = NavConstants.consumedArtifacts;
            }
            let selectedTabId: string = this._getViewFromHistoryState();
            this._updateHistoryState(selectedTabId);
            this._setSelectedTab(selectedTabId);

        });            
    }

    public render() {
        this._getImageDetails();
        if (this.imageUri && this.containerAlias && this.containerTag) {
            return (
                <ContainerImageDetails imageUri={this.imageUri} containerAlias={this.containerAlias} containerTag={this.containerTag} />
            );
        }
        else {
           return (this._getTabView());
        }
    }

    protected _getImageDetails(): void {
        this.imageUri = this.historyService.getState()[NavConstants.imageUri];
        this.containerAlias = this.historyService.getState()[NavConstants.alias];
        this.containerTag = this.historyService.getState()[NavConstants.tag];
    }

    private _getTabView(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow" tabProviders={this.tabProviders} selectedTabId={this.selectedTabId}>
                    <Header
                        backButtonProps={{ onClick: this._onBackClick }}
                        title={Resources.ArtifactsView_Header}
                        titleSize={TitleSize.Large}
                    />
                    <TabBar tabSize={TabSize.Tall} onSelectedTabChanged={this._onSelectedTabChanged} >
                    </TabBar>
                    <TabContent />
                </Page>
            </Surface>
        );
    }

    private _getArtifactsCount(): Promise<ArtifactsData | undefined> {
        let contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const additionalProperties = {
            buildId: this.historyService.getState()[RouteParameters.BuildId]
        }
        return contributionService.getDataAsync<ArtifactsData>("ms.vss-build-web.run-details-artifacts-data-provider", additionalProperties, true);
    }

    private _onBackClick = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>): void => {
        let pageContext = this.context.pageContext;
        let linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        let buildId: number = parseInt(this.historyService.getState()[RouteParameters.BuildId]);
        let url: string = linkingService.getBuildUrl(buildId);
        onClickFPS(pageContext, url, true);
    }

    private _updateHistoryState(selectedTabId?: string): void {
        let currentState = this.historyService.getState() || {};
        currentState[NavConstants.type] = selectedTabId || this.selectedTabId.value;
        this.historyService.replaceState(currentState);
    }

    private _getViewFromHistoryState(): string {
        let currentState = this.historyService.getState();
        if (currentState[NavConstants.type]) {
            return currentState[NavConstants.type];
        }  
        else {
            return this.defaultTab;
        }
    }

    private _setSelectedTab(selectedTabId: string): void {
        this.selectedTabId.value = selectedTabId || this.defaultTab;   
    }

    private _onSelectedTabChanged = (newTabId: string) => {
        if (!newTabId) {
           newTabId = this.defaultTab;
        }
        this.selectedTabId.value = newTabId;
        this._updateHistoryState(newTabId);
    };

    private tabProviders: ObservableCollection<IVssContributedTab> = new ObservableCollection<IVssContributedTab>();
    private readonly selectedTabId = new ObservableValue<string>("");
    private readonly historyService: IVssHistoryService;
    private imageUri: string;
    private containerAlias: string;
    private containerTag: string;
    private defaultTab: string = '';
}

Components.add("run-artifacts-content", ArtifactsTabView);
